const { base36EncodeNumber } = require('@/functions/math');
const { toSquashed } = require('@/functions/date');

/**
 * @constructor
 *
 * Creates strings to be used as references for carts and payments (with a suffix for attempt  number).
 * The strings are constructed according to a format rather than being just hashes or timestamps so that they can be
 * decoded and show useful information such as deviceID or companyID. We get time as a static value from the cart so
 * that there is always a link between the different types of reference.
 *
 * @param {CurrentDevice} currentDevice
 * @param {UserService} userService
 */
const ThirdPartyRefGenerator = function (
    currentDevice,
    userService
) {
    this.currentDevice = currentDevice;
    this.userService = userService;
};

ThirdPartyRefGenerator.prototype = {
    /**
     * @param {Cart} cart
     *
     * @return {string}
     */
    generateCartThirdPartyRef(cart) {
        let parts = [
            // companyID
            this.userService?.company?.ID || '',
            // deviceID
            this.currentDevice?.getDeviceId() || '',
            // Cart creation time.
            // Use the first digit of the milliseconds (tenths of a second) to make the reference more unique.
            // Any more than that should be unnecessary.
            // This will be 13 digits long (the same as a timestamp in milliseconds).
            toSquashed(cart.createdAt, 1),
        ];

        return parts.join('-');
    },

    /**
     * @param {Cart} cart
     *
     * @return {string}
     */
    generateOrderThirdPartyRef(cart) {
        let ref = cart.thirdPartyRef;

        if (cart.ordersAttempted > 0) {
            // If this is the second attempt or more at making an order for the cart,
            // add a suffix to keep the reference unique. Note that this will begin at 1, just like bookingRefs.
            // The original order is ACBD
            // The second attempt is ACBD.1
            // The third attempt is ACBD.2
            ref += '.' + cart.ordersAttempted;
        }

        return ref;
    },

    /**
     * @param {Cart|{paymentsAttempted: number, thirdPartyRef: string, thirdPartyID: string}} cart
     *
     * @return {string}
     */
    generatePaymentThirdPartyRef(cart) {
        cart.paymentsAttempted += 1;

        // Replace the hyphen with a 0 to make the reference numeric.
        return (cart.thirdPartyRef || '').replace(/-/g, '0')
            + '0'
            + cart.paymentsAttempted;
    },

    /**
     * Generate a base36 encoded string from an integer string so that we can represent the string in shorter fashion.
     * Introduced to generate an externalReferenceId for the Clover integration.
     *
     * @param {Cart} cart
     *
     * @return {string}
     */
    generateShortPaymentThirdPartyRef(cart) {
        // Time is too long. We don't need to be that accurate...
        const time = Math.floor(cart.createdAt.getTime() / 1000);

        const str = this.currentDevice.getDeviceId() + '0' + time + '0' + cart.paymentsAttempted;

        return base36EncodeNumber(parseInt(str));
    },
};

module.exports = ThirdPartyRefGenerator;
